import { OfferProperty, OrderDelivery } from '../types';

const AWAITING_RECEIPT_TRACKING_STATUSES = new Set<string>([
    'ready_for_pickup',
    'arrived_to_branch',
    'arrived_at_branch',
]);

export class OrdersService {
    static getOfferPropertyEntries(
        propertySet?: Record<string, string> | null,
        offerProperties: OfferProperty[] = [],
    ) {
        return Object.entries(propertySet || {})
            .filter(
                ([, value]) =>
                    typeof value === 'string' && value.trim().length > 0,
            )
            .map(([rawKey, rawValue]) => {
                const normalizedKey = rawKey.trim().toLowerCase();
                const property = offerProperties.find(
                    (offerProperty) =>
                        offerProperty.slug.toLowerCase() === normalizedKey ||
                        offerProperty.name.toLowerCase() === normalizedKey,
                );
                const option = property?.offer_property_options?.find(
                    (offerPropertyOption) =>
                        offerPropertyOption.slug === rawValue ||
                        offerPropertyOption.name.toLowerCase() ===
                            rawValue.toLowerCase(),
                );

                return {
                    key: property?.name || rawKey,
                    value: option?.name || rawValue,
                };
            });
    }

    static waitingForRecipient(
        status: string,
        delivery?: OrderDelivery | null,
    ): boolean {
        if (status !== 'shipped' || !delivery?.last_tracking_status) {
            return false;
        }

        return AWAITING_RECEIPT_TRACKING_STATUSES.has(
            delivery.last_tracking_status,
        );
    }

    static getStatusLabel(status: string, delivery?: OrderDelivery | null) {
        if (this.waitingForRecipient(status, delivery)) {
            return 'Доставлено та очікує отримання';
        }

        switch (status) {
            case 'pending':
                return 'Очікує підтвердження';
            case 'paid':
                return 'Оплачено';
            case 'awaiting_shipment':
                return delivery?.method === 'pickup'
                    ? 'Очікує передачі'
                    : 'Очікує відправки';
            case 'shipped':
                return 'Відправлено';
            case 'handed_over':
                return 'Передано';
            case 'delivered':
                return 'Отримано';
            case 'completed':
                return 'Виконано';
            case 'returned':
                return 'Повернено';
            case 'rejected':
                return 'Відхилено';
            case 'auto_cancelled':
                return 'Автоматично скасовано';
            case 'cancelled':
                return 'Скасовано';
            case 'dispute':
                return 'Суперечка';
            default:
                return status;
        }
    }

    static getStatusBadgeVariant(
        status: string,
        delivery?: OrderDelivery | null,
    ) {
        if (this.waitingForRecipient(status, delivery)) {
            return 'info';
        }

        switch (status) {
            case 'completed':
            case 'delivered':
                return 'success';
            case 'shipped':
                return 'info';
            case 'pending':
            case 'paid':
            case 'awaiting_shipment':
            case 'handed_over':
                return 'warning';
            case 'cancelled':
            case 'auto_cancelled':
                return 'neutral';
            case 'returned':
            case 'rejected':
            case 'dispute':
                return 'destructive';
            default:
                return 'outline';
        }
    }

    static getDeliveryMethodLabel(method: string) {
        switch (method) {
            case 'nova_poshta':
                return 'Нова Пошта (Відділення)';
            case 'ukrposhta':
                return 'Укрпошта';
            case 'pickup':
                return 'Самовивіз';
            default:
                return method;
        }
    }
}
